import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiUser,
  HiCamera,
  HiCheck,
  HiXMark,
  HiPencil,
  HiPhone,
  HiBuildingOffice2,
  HiMapPin,
  HiIdentification,
  HiBriefcase,
  HiAcademicCap,
} from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import {
  getUserSettings,
  updateProfileExtended,
  uploadProfilePhoto,
} from '../../services/settingsService';
import { toast } from '../../utils/toast';

export default function ProfileSettingsEnhanced() {
  const { currentUser } = useAuth();
  const { userRole } = useUserRole();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [profile, setProfile] = useState({});
  const [formData, setFormData] = useState({});

  const isAdmin = userRole === 'admin' || userRole === 'admins';
  const isSuperAdmin = userRole === 'superAdmin' || userRole === 'superAdmins';
  const isModerator = userRole === 'moderator';
  const isCoach = userRole === 'coach' || userRole === 'businessCoach';
  const isAlumni = userRole === 'alumni';
  const isClient = userRole === 'client' || userRole === 'entrepreneur';

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getUserSettings(currentUser.uid);
      setProfile(data);

      // Initialize form data with all possible fields
      setFormData({
        firstname: data.firstname || '',
        lastname: data.lastname || '',
        displayName: data.displayName || `${data.firstname || ''} ${data.lastname || ''}`.trim(),
        bio: data.bio || '',
        phone: data.phone || '',
        location: data.location || '',
        organization: data.organization || '',
        company: data.company || '',
        department: data.department || '',
        employeeId: data.employeeId || '',
        specialization: data.specialization || '',
        prefix: data.prefix || '',
        gender: data.gender || '',
        businessStage: data.businessStage || '',
        program: data.program || '',
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [currentUser.uid]);

  useEffect(() => {
    if (currentUser) {
      loadProfile();
    }
  }, [currentUser, loadProfile]);

  const handlePhotoChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const photoURL = await uploadProfilePhoto(currentUser.uid, file);
      setProfile(prev => ({ ...prev, photoURL }));
      toast.success('Profile photo updated');
    } catch (error) {
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.firstname?.trim() || !formData.lastname?.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    try {
      setSaving(true);

      // Prepare updates (only send changed fields)
      const updates = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== undefined && formData[key] !== profile[key]) {
          updates[key] = typeof formData[key] === 'string' ? formData[key].trim() : formData[key];
        }
      });

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setIsEditing(false);
        return;
      }

      await updateProfileExtended({
        uid: currentUser.uid,
        ...updates,
      });

      setProfile(prev => ({
        ...prev,
        ...updates,
      }));

      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstname: profile.firstname || '',
      lastname: profile.lastname || '',
      displayName: profile.displayName || '',
      bio: profile.bio || '',
      phone: profile.phone || '',
      location: profile.location || '',
      organization: profile.organization || '',
      company: profile.company || '',
      department: profile.department || '',
      employeeId: profile.employeeId || '',
      specialization: profile.specialization || '',
      prefix: profile.prefix || '',
      gender: profile.gender || '',
      businessStage: profile.businessStage || '',
      program: profile.program || '',
    });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white p-12 shadow-lg">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="h-8 w-8 rounded-full border-4 border-blue-600 border-t-transparent"
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/30">
              <HiUser className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-black">Profile Information</h2>
              <p className="text-sm text-gray-600">
                Update your personal details and professional information
              </p>
            </div>
          </div>
          {!isEditing && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700"
            >
              <HiPencil className="h-4 w-4" />
              Edit Profile
            </motion.button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Photo */}
          <div className="flex flex-col items-center gap-6 border-b border-gray-200 pb-8 sm:flex-row">
            <div className="relative">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative h-32 w-32 overflow-hidden rounded-full border-4 border-gray-200 bg-gray-100 shadow-lg"
              >
                {profile.photoURL && profile.photoURL !== 'null' ? (
                  <img
                    src={profile.photoURL}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200">
                    <HiUser className="h-16 w-16 text-blue-600" />
                  </div>
                )}
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="h-8 w-8 rounded-full border-4 border-white border-t-transparent"
                    />
                  </div>
                )}
              </motion.div>
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                onChange={handlePhotoChange}
                disabled={uploading}
                className="hidden"
              />
              <motion.label
                htmlFor="photo-upload"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="absolute bottom-0 right-0 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-blue-600 text-white shadow-lg ring-4 ring-white transition-colors hover:bg-blue-700"
              >
                <HiCamera className="h-5 w-5" />
              </motion.label>
            </div>
            <div className="text-center sm:text-left">
              <p className="text-sm font-semibold text-black">Profile Photo</p>
              <p className="mt-1 text-xs text-gray-600">
                Upload a professional photo (JPG, PNG, or GIF, max 5MB)
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-700">
                  {profile.collection}
                </span>
                <span className="rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-700">
                  {profile.role || userRole}
                </span>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div>
            <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-black">
              <HiUser className="h-5 w-5 text-blue-600" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Prefix (Admin/SuperAdmin/Moderator) */}
              {(isAdmin || isSuperAdmin || isModerator) && (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-black">Prefix</label>
                  <select
                    value={formData.prefix}
                    onChange={e => setFormData({ ...formData, prefix: e.target.value })}
                    disabled={!isEditing}
                    className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                  >
                    <option value="">Select</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                    <option value="Prof">Prof</option>
                  </select>
                </div>
              )}

              {/* First Name */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">
                  First Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.firstname}
                  onChange={e => setFormData({ ...formData, firstname: e.target.value })}
                  disabled={!isEditing}
                  className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                  placeholder="Enter first name"
                  required
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">
                  Last Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.lastname}
                  onChange={e => setFormData({ ...formData, lastname: e.target.value })}
                  disabled={!isEditing}
                  className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                  placeholder="Enter last name"
                  required
                />
              </div>

              {/* Display Name */}
              <div className="sm:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-black">Display Name</label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={e => setFormData({ ...formData, displayName: e.target.value })}
                  disabled={!isEditing}
                  className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                  placeholder="How you want to be called"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-black">
              <HiPhone className="h-5 w-5 text-blue-600" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Email (Read-only) */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">Email Address</label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-600"
                />
                <p className="mt-2 text-xs text-gray-500">Go to Email tab to change</p>
              </div>

              {/* Phone */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">Phone Number</label>
                <div className="relative">
                  <HiPhone className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    disabled={!isEditing}
                    className="w-full rounded-lg border-2 border-gray-300 bg-white py-3 pl-10 pr-4 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                    placeholder="+267 1234 5678"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">Location</label>
                <div className="relative">
                  <HiMapPin className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={formData.location}
                    onChange={e => setFormData({ ...formData, location: e.target.value })}
                    disabled={!isEditing}
                    className="w-full rounded-lg border-2 border-gray-300 bg-white py-3 pl-10 pr-4 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                    placeholder="City, Country"
                  />
                </div>
              </div>

              {/* Gender (Admin/SuperAdmin/Moderator) */}
              {(isAdmin || isSuperAdmin || isModerator) && (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-black">Gender</label>
                  <select
                    value={formData.gender}
                    onChange={e => setFormData({ ...formData, gender: e.target.value })}
                    disabled={!isEditing}
                    className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Professional Information */}
          <div>
            <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-black">
              <HiBriefcase className="h-5 w-5 text-blue-600" />
              Professional Information
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Organization/Company */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-black">
                  {isModerator || isAdmin || isSuperAdmin ? 'Company' : 'Organization'}
                </label>
                <div className="relative">
                  <HiBuildingOffice2 className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={
                      isModerator || isAdmin || isSuperAdmin
                        ? formData.company
                        : formData.organization
                    }
                    onChange={e => {
                      if (isModerator || isAdmin || isSuperAdmin) {
                        setFormData({ ...formData, company: e.target.value });
                      } else {
                        setFormData({ ...formData, organization: e.target.value });
                      }
                    }}
                    disabled={!isEditing}
                    className="w-full rounded-lg border-2 border-gray-300 bg-white py-3 pl-10 pr-4 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                    placeholder={
                      isModerator || isAdmin || isSuperAdmin ? 'Company name' : 'Organization name'
                    }
                  />
                </div>
              </div>

              {/* Department (Admin/SuperAdmin/Moderator) */}
              {(isAdmin || isSuperAdmin || isModerator) && (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-black">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={e => setFormData({ ...formData, department: e.target.value })}
                    disabled={!isEditing}
                    className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                    placeholder="e.g., Business Development"
                  />
                </div>
              )}

              {/* Employee ID (Admin/SuperAdmin/Moderator) */}
              {(isAdmin || isSuperAdmin || isModerator) && (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-black">Employee ID</label>
                  <div className="relative">
                    <HiIdentification className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData.employeeId}
                      onChange={e => setFormData({ ...formData, employeeId: e.target.value })}
                      disabled={!isEditing}
                      className="w-full rounded-lg border-2 border-gray-300 bg-white py-3 pl-10 pr-4 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="Employee ID"
                    />
                  </div>
                </div>
              )}

              {/* Specialization (Coach) */}
              {isCoach && (
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-black">
                    Specialization
                  </label>
                  <input
                    type="text"
                    value={formData.specialization}
                    onChange={e => setFormData({ ...formData, specialization: e.target.value })}
                    disabled={!isEditing}
                    className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
                    placeholder="e.g., Finances, Agriculture, Technology"
                  />
                </div>
              )}

              {/* Business Stage (Alumni/Client) */}
              {(isAlumni || isClient) && (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-black">
                    Business Stage
                  </label>
                  <input
                    type="text"
                    value={formData.businessStage}
                    disabled
                    className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-600"
                  />
                  <p className="mt-1 text-xs text-gray-500">Managed by administrators</p>
                </div>
              )}

              {/* Program (Alumni/Client) */}
              {(isAlumni || isClient) && (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-black">Program</label>
                  <div className="relative">
                    <HiAcademicCap className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData.program}
                      disabled
                      className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 py-3 pl-10 pr-4 text-sm font-medium text-gray-600"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Assigned by administrators</p>
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-black">Bio / About Me</label>
            <textarea
              value={formData.bio}
              onChange={e => setFormData({ ...formData, bio: e.target.value })}
              disabled={!isEditing}
              rows={4}
              className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-black transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
              placeholder="Tell us about yourself, your experience, and expertise..."
              maxLength={500}
            />
            <p className="mt-2 text-xs text-gray-500">{formData.bio?.length || 0}/500 characters</p>
          </div>

          {/* Action Buttons */}
          <AnimatePresence>
            {isEditing && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex justify-end gap-3 border-t border-gray-200 pt-6"
              >
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-black transition-all hover:bg-gray-50 disabled:opacity-50"
                >
                  <HiXMark className="h-4 w-4" />
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <HiCheck className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </div>
  );
}
